/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';

import {EXECUTION_STATUS, OBJECT_STATE} from '../common/constants/resource-constants';
import {Uuid} from '../../components/util/uuid';
import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';

export interface ReportExecutionModel extends SignalAdminResourceModel {
  [key: string]: any;
}

@Injectable({providedIn: 'root'})
export class ReportExecutionApiService extends SignalAdminResourceApiBase<ReportExecutionModel> {
  constructor(client: AdnApiClientService) {
    super(client, 'reports');
  }

  start(params: Partial<ReportExecutionModel>): Observable<ReportExecutionModel> {
    return this.save({...params, id: Uuid.generate()} as ReportExecutionModel);
  }

  listRelated(params: Record<string, any>): Observable<SignalAdminResourcePage<ReportExecutionModel>> {
    return this.client.request<SignalAdminResourcePage<ReportExecutionModel>>({
      method: 'GET',
      url: this.resourceUri('reports'),
      params: {
        includeHidden: false,
        includeActive: true,
        includeInactive: false,
        orderBy: 'name',
        pageSize: 1001,
        ...params
      }
    });
  }

  listByIds(ids: string[]): Observable<SignalAdminResourcePage<ReportExecutionModel>> {
    const selectedIds = ids.slice(0, 1001);
    return this.listRelated({
      id: selectedIds.join(';'),
      pageSize: selectedIds.length
    });
  }

  cancel(id: string): Observable<ReportExecutionModel> {
    return this.client.request<ReportExecutionModel>({
      method: 'POST',
      url: this.resourceUri(`reports/${encodeURIComponent(id)}`),
      body: {id, cancelRequested: true}
    });
  }

  delete(id: string): Observable<ReportExecutionModel> {
    return this.client.request<ReportExecutionModel>({
      method: 'POST',
      url: this.resourceUri(`reports/${encodeURIComponent(id)}`),
      body: {id, cancelRequested: true, objectState: OBJECT_STATE.hidden}
    }).pipe(map((execution) => ({...execution, status: EXECUTION_STATUS.cancelled})));
  }

  isAwaiting(execution: ReportExecutionModel): boolean {
    return [
      EXECUTION_STATUS.pending,
      EXECUTION_STATUS.queued,
      EXECUTION_STATUS.submitted,
      EXECUTION_STATUS.processing
    ].includes(execution.status);
  }
}
